/**
 * 🔴 #569 (13/10/2026) — o aviso da conferência diária dos cadastros com o Omie, no topo da lista de Clientes, só para administrador.
 *
 * Pedido do Matheus: *"alteração feita no Omie tem que chegar ao sistema, nas duas empresas, sempre"* — e, se não chegar, alguém tem de
 * saber. A conferência compara cada cadastro daqui com o do Omie uma vez por dia (e depois do botão de sincronizar); aqui ela vira um
 * aviso com a quantidade e a lista. ❌ Sem e-mail e sem WhatsApp. Sem divergência e com as duas empresas em dia, o aviso não aparece.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, Button } from '@/components/ui';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/format-date';

interface CampoDivergente {
  rotulo: string;
  daqui: string | null;
  doOmie: string | null;
  motivo?: string;
}
interface Conferencia {
  conferidoEmIso: string;
  medidos: number;
  divergentes: number;
  itens: Array<{ clienteId: string; nome: string; campos: CampoDivergente[] }>;
  empresas: Array<{ empresaId: string; lidaEmIso: string | null; atrasada: boolean }>;
}

const NOME_DA_EMPRESA: Record<string, string> = {
  'empresa-igm-ag': 'AG DOS SANTOS',
  'empresa-igm-mussi': 'MATHEUS MUSSI',
};

export function AvisoConferenciaOmie({ ehAdmin }: { ehAdmin: boolean }) {
  const [aberto, setAberto] = useState(false);
  const [data, setData] = useState<{ conferencia: Conferencia | null; atrasada: boolean } | null>(null);
  // Uma leitura ao abrir a tela (a conferência muda uma vez por dia). Falha de rede = sem aviso: o aviso é ajuda, não trava.
  useEffect(() => {
    if (!ehAdmin) return;
    let vivo = true;
    Promise.resolve()
      .then(() => api.get<{ conferencia: Conferencia | null; atrasada: boolean }>('/api/v1/clientes/conferencia-omie'))
      .then((r) => {
        if (vivo && r) setData(r);
      })
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [ehAdmin]);
  if (!ehAdmin || !data) return null;
  const c = data.conferencia;
  const empresasAtrasadas = (c?.empresas ?? []).filter((e) => e.atrasada);
  if (c && c.divergentes === 0 && empresasAtrasadas.length === 0 && !data.atrasada) return null;

  return (
    <Alert variant="warning" rail data-testid="clientes-conferencia-omie" className="mb-4">
      <div className="space-y-1 text-sm">
        <p className="font-semibold">Cadastros diferentes do Omie</p>
        {!c ? (
          <p>A conferência com o Omie ainda não rodou.</p>
        ) : (
          <>
            {data.atrasada && (
              <p data-testid="clientes-conferencia-atrasada">
                A última conferência é de {formatDateTime(c.conferidoEmIso)}: a leitura do Omie parou nas duas empresas.
              </p>
            )}
            {empresasAtrasadas.map((e) => (
              <p key={e.empresaId} data-testid="clientes-conferencia-empresa-atrasada">
                A empresa {NOME_DA_EMPRESA[e.empresaId] ?? e.empresaId} não é lida do Omie
                {e.lidaEmIso ? ` desde ${formatDateTime(e.lidaEmIso)}` : ''}.
              </p>
            ))}
            {c.divergentes > 0 && (
              <p data-testid="clientes-conferencia-quantidade">
                {c.divergentes === 1 ? '1 cadastro está' : `${c.divergentes} cadastros estão`} diferente
                {c.divergentes === 1 ? '' : 's'} do Omie (conferido em {formatDateTime(c.conferidoEmIso)}).{' '}
                <Button variant="link" size="sm" onClick={() => setAberto((a) => !a)} data-testid="clientes-conferencia-ver">
                  {aberto ? 'Esconder a lista' : 'Ver a lista'}
                </Button>
              </p>
            )}
            {aberto && (
              <ul className="mt-2 space-y-2" data-testid="clientes-conferencia-lista">
                {c.itens.map((i) => (
                  <li key={i.clienteId}>
                    <Link to={`/admin/clientes-v2/${i.clienteId}`} className="font-medium underline">
                      {i.nome}
                    </Link>
                    <ul className="ml-4 list-disc">
                      {i.campos.map((f, k) => (
                        <li key={k}>
                          {f.rotulo}: aqui “{f.daqui ?? 'vazio'}”, no Omie “{f.doOmie ?? 'vazio'}”
                          {f.motivo ? ` — ${f.motivo}` : ''}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </Alert>
  );
}
